import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, BarChart3, Check, Clock, Filter, Loader2, Lock, Mail, MessageSquare, Pause, Play, Plus, Repeat, Siren, Sparkles, UserPlus, Users, Workflow } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];

/**
 * One row treatment for every list in these screens. A faint warm fill plus a
 * 1px contact shadow gives each row a surface, which is the difference between
 * a product screenshot and an outlined box.
 */
const ROW = 'rounded-xl border border-[#EFEAE1] bg-[#FCFAF6] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_1px_2px_-1px_rgba(43,33,26,0.06)]';
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-750" data-mp="e3sqo471fm69">
      {/* No eyebrow, and left-aligned to match the co-worker section. The
       "Workspace" label named the section before the headline could say
       anything, and the headline already states the claim. Left-aligning also
       puts the intro on the same edge as the primitives list directly beneath
       it, so the whole section reads down one axis instead of the copy being
       centred above a left-aligned column. */}
      <SectionIntro className="max-w-3xl text-left" data-id="element-751" data-mp="1cr1oi3girdxh">
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-752" data-mp="1cof9jiratw58">
          One place to manage
          <br data-id="element-753" data-mp="1lqeps8uhow74" />
          AI within your company
        </h2>
        <p className="mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-754" data-mp="1qi6ioq14be0f">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-755" data-mp="25f7asun81azf">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1.5" data-id="element-756" data-mp="3l0hooejoy2b2">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-757" data-mp="3g1511rkzwr7f">
                {/* Selection is carried by an orange rail and a warm fill
                 rather than a floating white card — the card read as a second
                 panel competing with the app frame beside it. */}
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`relative w-full rounded-xl py-4 pl-5 pr-4 text-left transition-[background-color,color] duration-200 ease-out ${on ? 'bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.08)] ring-1 ring-[#EFEAE1]' : 'hover:bg-[#F6F2EA]'}`} data-id="element-758" data-mp="7ktgvv1o9z8f">
                  <span className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-full transition-colors duration-200 ease-out ${on ? 'bg-[#EA580C]' : 'bg-transparent'}`} aria-hidden="true" data-id="element-759" data-mp="vaxlo5qlk510" />
                  <span className="flex items-center gap-2.5" data-id="element-760" data-mp="b2frsaxxztau">
                    <p.icon size={17} strokeWidth={1.9} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-761" data-mp="g07mtdtvvvj9" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-762" data-mp="13qc8otiqkxr">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2.5 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-763" data-mp="tsxiyb34mgu3">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        {/* No app nav on any of the five. The numbered rail to the left is
         already the navigation for this section, so a sidebar inside the frame
         put two columns of it around one screen — and each screen states what
         it is in its own heading anyway. */}
        <AppChrome sidebar={false} data-id="element-764" data-mp="3bvc9a8hg13te">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className={active === 'chats' ? 'flex h-full flex-col' : 'px-6 py-6 md:px-9 md:py-8'} data-id="element-765" data-mp="3byghdgy14jpc">
            {active === 'chats' && <ChatsScreen data-id="element-766" data-mp="2s9pkrmzo6t6u" />}
            {active === 'shared' && <SharedScreen data-id="element-767" data-mp="2s6bmapu4n44o" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-768" data-mp="2o69t752gjj10" />}
            {active === 'pulses' && <PulsesScreen data-id="element-769" data-mp="7i5lrxvirjb4" />}
            {active === 'skills' && <SkillsScreen data-id="element-770" data-mp="7gujy3lmwlmn" />}
          </motion.div>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  // A real app view states what you're looking at and then rules off. The
  // hairline is what stops these reading as floating wireframe blocks.
  return <div className="mb-6 border-b border-[#F1ECE3] pb-4" data-id="element-771" data-mp="31c8cnwc1jfcq">
      <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-772" data-mp="36ataxq7qfoyj">
        {title}
      </h3>
      <p className="mt-1.5 text-[12.5px] tabular-nums text-neutral-500" data-id="element-773" data-mp="3a8r2gzoz6n53">
        {meta}
      </p>
    </div>;
}

// The answer, as the thing a revenue lead would actually want on screen: which
// accounts, what they're worth, and the one fact that makes each a risk. Prose
// alone made the reader reassemble a table in their head.
const AT_RISK = [{
  account: 'Northwind Labs',
  value: '$84k',
  closes: '12 Sep',
  reason: 'No exec contact in 41 days · 2 tickets open'
}, {
  account: 'Meridian Health',
  value: '$61k',
  closes: '28 Sep',
  reason: 'Champion left in July · no replacement named'
}, {
  account: 'Fairline Group',
  value: '$37k',
  closes: '30 Sep',
  reason: 'Usage down 38% month over month'
}];
export function ChatsScreen() {
  return <div className="flex h-full min-h-[420px] flex-col" data-id="element-774" data-mp="1medqbhlndjg6">
      {/* Thread header — what this chat is and which group owns it. */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#F1ECE3] px-5 py-3.5 md:px-7" data-id="element-775" data-mp="1idtx2p472zzb">
        <div className="min-w-0" data-id="element-776" data-mp="18djhwyylnihi">
          <h3 className="truncate font-title text-[16px] font-semibold tracking-[-0.01em] text-[#2B211A]" data-id="element-777" data-mp="18dt7wmjn9pn7">
            Renewal risk in Salesforce
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-778" data-mp="13l07ubz4f5hg">
            <span className="flex size-4 items-center justify-center rounded bg-[#134E4A] text-[7px] font-bold text-white" data-id="element-779" data-mp="ylds9vvtld5h">
              SL
            </span>
            Sales
            <span className="text-[#D8CFC2]" data-id="element-780" data-mp="pma80okx2rpe">·</span>
            Today, 08:42
          </p>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-500 sm:inline-flex" data-id="element-781" data-mp="koqszmgeaawe">
          <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-782" data-mp="2dighjng97n3b" />
          8 tools connected
        </span>
      </div>

      {/* Transcript */}
      <div className="flex-1 space-y-5 overflow-hidden px-5 py-5 md:px-7" data-id="element-783" data-mp="24j1r2o037cit">
        <div className="flex justify-end" data-id="element-784" data-mp="24h8l7axaswbv">
          <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-785" data-mp="1ptne75xaycyo">
            Which renewals due this quarter look at risk?
          </p>
        </div>

        <div className="flex gap-3" data-id="element-786" data-mp="1guiie7d4kxq7">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#2B211A]" data-id="element-787" data-mp="1gre60mpiavog">
            <Sparkles size={13} strokeWidth={2} className="text-white" data-id="element-788" data-mp="1kspzj3bi411x" />
          </span>
          <div className="min-w-0 flex-1 space-y-3.5" data-id="element-789" data-mp="16xe5yz930gml">
            {/* What it read, before what it concluded. */}
            <div className="relative space-y-1.5 pl-3.5 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[2px] before:rounded-full before:bg-[#EFEAE1] before:content-['']" data-id="element-790" data-mp="1bw8p62ayo778">
              {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-791" data-mp="x9s3pvokj5dm">
                  <span className="flex size-[18px] shrink-0 items-center justify-center rounded-md border border-[#EFEAE1] bg-white" data-id="element-792" data-mp="1b5dnnofy2yz6">
                    <BrandLogo slug={slug} className="size-[11px]" data-id="element-793" data-mp="1kxt58knbxljj" />
                  </span>
                  {label}
                  <Check size={11} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-794" data-mp="1fz83uhwccvti" />
                </p>)}
            </div>

            <p className="text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-795" data-mp="1tyhmrpbxm416">
              <strong className="font-semibold text-[#2B211A]" data-id="element-796" data-mp="1pxe30rqrjnuo">
                Three of eleven look at risk
              </strong>{' '}
              — $182k of $604k closing this quarter.
            </p>

            <ul className="divide-y divide-[#F1ECE3] overflow-hidden rounded-xl border border-[#EFEAE1] bg-[#FCFAF6]" data-id="element-797" data-mp="3afwp4nfr5bkw">
              {AT_RISK.map(r => <li key={r.account} className="flex items-start gap-3 px-3.5 py-2.5" data-id="element-798" data-mp="35f90g8ezx6tu">
                  <span className="mt-[5px] size-1.5 shrink-0 rounded-full bg-red-400" data-id="element-799" data-mp="30kbr10dt0vgf" />
                  <span className="min-w-0 flex-1" data-id="element-800" data-mp="20d3a9heuvfv">
                    <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-801" data-mp="3t6o7uflmqeby">
                      {r.account}
                    </span>
                    <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-802" data-mp="3o6a0kzrur8ez">
                      {r.reason}
                    </span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-803" data-mp="2ctzfzgn9iilm">
                    <span className="block text-[13px] font-semibold tabular-nums text-[#2B211A]" data-id="element-804" data-mp="28rcjv3jk6bim">
                      {r.value}
                    </span>
                    <span className="block text-[11px] tabular-nums text-neutral-400" data-id="element-805" data-mp="oav84sgi39pz">
                      {r.closes}
                    </span>
                  </span>
                </li>)}
            </ul>

            <p className="text-[13px] text-neutral-500" data-id="element-806" data-mp="x8no7yu1qlef">
              I've drafted outreach for each owner — want me to send it?
            </p>
            {/* Offering the next move as buttons is what makes this a working
             surface rather than a transcript of one. */}
            <div className="flex flex-wrap gap-2" data-id="element-807" data-mp="1bxbf3rsy66y1">
              <span className="rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-semibold text-white" data-id="element-808" data-mp="1g0pwalsmgtd2">
                Send all three
              </span>
              <span className="rounded-lg border border-[#E7E0D4] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#2B211A]" data-id="element-809" data-mp="1kyrpw0nocb2s">
                Review drafts
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 px-5 pb-5 md:px-7" data-id="element-810" data-mp="tvnuisrp4lnw">
        <div className="flex items-center gap-2.5 rounded-2xl border border-[#E7E0D4] bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-811" data-mp="tyjt8jbdbede">
          <Plus size={16} strokeWidth={1.9} className="shrink-0 text-neutral-400" data-id="element-812" data-mp="12xdtcjnwqmvp" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-400" data-id="element-813" data-mp="xxj3mjfvc3c8">
            Ask anything across your connected tools
          </span>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-814" data-mp="1ctgze0lvr5nx">
            <ArrowUp size={13} strokeWidth={2.6} className="text-white" data-id="element-815" data-mp="17u4c5er3jwkt" />
          </span>
        </div>
      </div>
    </div>;
}

// Named after the departments any company would recognise. The previous set
// ("Giga Master", "NoContact Ops") was Giga's own internal workspace, so a
// visitor had to decode what each one was before the permissions idea landed.
//
// Counts were the wrong content here: "14 tracks · 96 chats" says a group has
// stuff in it, which nobody doubted, and says nothing about what the screen is
// actually for. What a group IS, is a set of people plus the tools and work
// they can reach — so each card names those instead.
// ACCESS IS THE VARIABLE, so it has to vary. Every row carrying an identical
// "Members only" pill said nothing — a column where every cell reads the same
// is not information. Naming how each group is JOINED (invite only, by
// request, open to all) is the fact the screen exists to show, and the counts
// beside the name give each group a size so they read as real places rather
// than as four labels.
type GroupAccess = 'invite' | 'request' | 'open';
const GROUPS: Array<{
  name: string;
  color: string;
  access: GroupAccess;
  people: number;
  tracks: number;
  chats: number;
}> = [{
  name: 'Leadership',
  color: '#1E3A5F',
  access: 'invite',
  people: 5,
  tracks: 14,
  chats: 96
}, {
  name: 'Sales',
  color: '#134E4A',
  access: 'request',
  people: 7,
  tracks: 33,
  chats: 214
}, {
  name: 'Marketing',
  color: '#7C2D12',
  access: 'open',
  people: 6,
  tracks: 12,
  chats: 88
}, {
  name: 'Customer Success',
  color: '#3F3A2E',
  access: 'request',
  people: 4,
  tracks: 21,
  chats: 131
}];
const ACCESS: Record<GroupAccess, {
  label: string;
  icon: typeof Lock;
  className: string;
  iconClass: string;
}> = {
  invite: {
    label: 'Invite only',
    icon: Lock,
    className: 'border-[#EFEAE1] bg-[#FBF8F3] text-neutral-600',
    iconClass: 'text-neutral-400'
  },
  request: {
    label: 'By request',
    icon: UserPlus,
    className: 'border-[#EFEAE1] bg-[#FBF8F3] text-neutral-600',
    iconClass: 'text-neutral-400'
  },
  // The only open group is the only coloured pill — the exception is the thing
  // an admin scans this list for.
  open: {
    label: 'Open to all',
    icon: Users,
    className: 'border-[#F3DECB] bg-[#FFF6EF] text-[#C2410C]',
    iconClass: 'text-[#EA580C]'
  }
};
export function SharedScreen() {
  return <div data-id="element-816" data-mp="2ap2qevucuqvp">
      <ScreenHead title="Groups & Permissions" meta="Who can see what — members get every track, chat and connection inside" data-id="element-817" data-mp="2apch6ln8522g" />
      {/* ONE COLUMN, not two. Access is a column of values you compare down the
       list; in a 2×2 grid there is no column to run your eye down. */}
      <ul className="space-y-2.5" data-id="element-818" data-mp="2og7tk7wxm1as">
        {GROUPS.map(g => {
        const access = ACCESS[g.access];
        const AccessIcon = access.icon;
        return <li key={g.name} className="flex items-center gap-3.5 rounded-2xl border border-[#EFEAE1] bg-[#FCFAF6] p-3.5 sm:gap-4 sm:p-4" data-id="element-819" data-mp="1rr9x8l9j9vbi">
              {/* A glyph, not initials. Two letters in a coloured tile read as
               somebody's avatar, and a group is not a person — the people icon
               says "several of us" at a glance. Squared off, where a person's
               avatar is round, so the two never get confused. */}
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" style={{
            backgroundColor: g.color
          }} data-id="element-820" data-mp="3h4wnnmzkl6d1">
                <Users size={18} strokeWidth={2} className="text-white" data-id="element-821" data-mp="2mifvk6zr51dc" />
              </span>

              <div className="min-w-0 flex-1" data-id="element-822" data-mp="31f700uoa939t">
                <p className="truncate text-[15px] font-bold leading-tight text-[#2B211A]" data-id="element-823" data-mp="31cukeo5zz4zx">
                  {g.name}
                </p>
                <p className="mt-1 truncate text-[12.5px] tabular-nums text-neutral-500" data-id="element-824" data-mp="28pqaq5vym2ht">
                  {g.people} people <span className="text-neutral-300" data-id="element-825" data-mp="23ry7ifpv6tyb">·</span>{' '}
                  {g.tracks} tracks <span className="text-neutral-300" data-id="element-826" data-mp="2ift2y3v9bcf3">·</span>{' '}
                  {g.chats} chats
                </p>
              </div>

              <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold ${access.className}`} data-id="element-827" data-mp="2doksd4i6xl0c">
                <AccessIcon size={12} strokeWidth={2.2} className={access.iconClass} data-id="element-828" data-mp="1l0xa3w4e76gv" />
                {access.label}
              </span>
            </li>;
      })}
      </ul>
    </div>;
}
const OWNER_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/b394cd62-6cbf-40a2-98a1-4af4a23e89dd.jpg';

// Every step names the tools it touches — a step with no marks left a visible
// gap in the column and read as a missing image rather than a deliberate blank.
const STEP_TOOLS: string[][] = [['jira', 'linear'], ['intercom', 'salesforce'], ['notion'], ['slack', 'gmail']];
export function WorkflowsScreen() {
  const steps = ['Pull what shipped, slipped and stalled from Jira and Linear', 'Check every account flagged in support or gone quiet', 'Draft the update into the team space — wins, risks, decisions', 'Post to #team-update and email the leadership list'];
  return <div data-id="element-829" data-mp="ec48jis0wj2k">
      {/* Name, then one line of what it produces, then the health of it as
       tags on the right. You should not have to read the steps to know why
       this workflow exists. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-[#F1ECE3] pb-5" data-id="element-830" data-mp="2yy5vf12xjqin">
        <div className="min-w-0" data-id="element-831" data-mp="3loqlm15h9iin">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-832" data-mp="3lnp87q0zojxr">
            Monday morning update
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#6B5D52]" data-id="element-833" data-mp="3co27z7jzxk6a">
            Every Monday, 08:00
            <span className="text-[#D8CFC2]" data-id="element-834" data-mp="d9gluzp28pe1">·</span>
            <BrandLogo slug="slack" className="size-3.5" data-id="element-835" data-mp="db1hksn42edh" />
            <span className="font-medium text-[#2B211A]" data-id="element-836" data-mp="98el2efhw59y">#team-update</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-837" data-mp="4flgprnxuyye">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/20 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-700" data-id="element-838" data-mp="3vn6b5zouwjq1">
            <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-839" data-mp="wyr34i4gfie9" />
            Active
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-840" data-mp="s4d5e92bkz6f">
            <Repeat size={11} className="text-neutral-400" data-id="element-841" data-mp="14ijictpc6f0z" />
            26 runs
          </span>
          {/* The bar earns its place: 96% means more with 25 of 26 ticks
           filled beside it than as a number on its own. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-842" data-mp="1ightzrc9vxcs">
            <span className="flex items-end gap-[2px]" data-id="element-843" data-mp="1ncyi4ljh2bpv">
              {Array.from({
              length: 8
            }, (_, i) => <span key={i} className={`h-2.5 w-[2.5px] rounded-full ${i === 5 ? 'bg-red-400' : 'bg-emerald-500/80'}`} data-id="element-844" data-mp="1x4usauyu1sag" />)}
            </span>
            96%
          </span>
        </div>
      </div>

      {/* Who owns it and who can see it — the questions the steps can't
       answer. The read/write strip that sat here only restated them. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-7 gap-y-3" data-id="element-845" data-mp="373zff0pic2h2">
        <span className="flex items-center gap-2.5" data-id="element-846" data-mp="3c0pw9q94z23j">
          <img src={OWNER_AVATAR} alt="" aria-hidden="true" className="size-7 shrink-0 rounded-full object-cover shadow-[0_0_0_1px_rgba(43,33,26,0.08)]" data-id="element-847" data-mp="3c3cdkhltwvyy" />
          <span data-id="element-848" data-mp="3l38t9tq8qkhv">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-849" data-mp="3l3qyvgysmb2g">
              Owner
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-850" data-mp="3v0dgzxzqio0s">
              Sarah Chen
            </span>
          </span>
        </span>
        {/* "Shared with" read as the audience for the update — but the update
         goes to #team-update, which the subtitle already says. The useful fact
         is permission: who can run and edit this workflow. Same group tile the
         Groups screen uses, so a group reads as one object everywhere. */}
        <span className="flex items-center gap-2.5" data-id="element-851" data-mp="hlqs254z4bqf">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-xl bg-[#1E3A5F] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-852" data-mp="rpnv1w7ntun2">
            <Users size={13} strokeWidth={2} className="text-white" data-id="element-853" data-mp="hqyafvodpw34" />
          </span>
          <span data-id="element-854" data-mp="dpmktq5i9aux">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-855" data-mp="dq4o36ql4pcc">
              Can run and edit
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-856" data-mp="1af3wclr65r4j">
              Leadership · 5 people
            </span>
          </span>
        </span>
      </div>

      <p className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-857" data-mp="10orfb3ikcg35">
        Steps
      </p>
      {/* A rail behind the numbers makes the steps read as one sequence
       rather than four unrelated cards. */}
      <ol className="relative space-y-2.5 before:absolute before:bottom-7 before:left-[30px] before:top-7 before:w-px before:bg-[#EFEAE1] before:content-['']" data-id="element-858" data-mp="1t87xes6pv696">
        {steps.map((s, i) => <li key={s} className={`relative flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-859" data-mp="3ioad5s3sx6le" data-mpi={i}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[11px] font-bold tabular-nums text-white shadow-[0_1px_2px_rgba(43,33,26,0.3)]" data-id="element-860" data-mp="3nltsywg5drfg">
              {i + 1}
            </span>
            <span className="flex-1 text-[13px] leading-snug text-[#3D3229]" data-id="element-861" data-mp="2zx0vgvjvnbiq">
              {s}
            </span>
            {/* The tools each step touches, stated once where they're used
             rather than duplicated in a strip above the list. */}
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-862" data-mp="2q0e9u4jq8gfn">
              {(STEP_TOOLS[i] ?? []).map(t => <BrandLogo key={t} slug={t} className="size-[15px] opacity-80" data-id="element-863" data-mp="2uyg3tkioy05w" />)}
            </span>
          </li>)}
      </ol>
      {/* Run now is the screen's primary action and was sized like a tag.
       It now matches the buttons used elsewhere in the product screens. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#EFEAE1] pt-4" data-id="element-864" data-mp="2dnu08t0wyz7n">
        <span className="inline-flex items-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-865" data-mp="28qtw4nnmk6j6">
          <Play size={13} fill="currentColor" data-id="element-866" data-mp="2imwynume276n" />
          Run now
        </span>
        <span className="text-[12px] tabular-nums text-neutral-500" data-id="element-867" data-mp="2webrsua0forf">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
export function PulsesScreen() {
  // A pulse only matters if you know where its output lands, so the
  // destination sits on the row with the mark of the tool it posts to.
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    to: '#revenue',
    logo: 'slack',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    to: 'Leadership list',
    logo: 'gmail',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    to: '#support-escalations',
    logo: 'slack',
    // Running right now. State belongs on the thing it describes, not in a
    // banner underneath that made you match a name back to a row.
    running: 'Step 2 of 4 · reading Intercom',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    to: 'Customer success',
    logo: 'microsoftteams',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  // One starts paused, so the control reads as a real state rather than a
  // decorative button — and the paused row visibly stops reporting.
  const [paused, setPaused] = React.useState<Record<string, boolean>>({
    'Churn signal digest': true
  });
  return <div data-id="element-868" data-mp="2p9pliq33um0c">
      {/* The subtitle defines the primitive rather than counting rows: someone
       meeting Pulses for the first time needs to know what one is, and the
       list below already shows how many are running. */}
      <ScreenHead title="Pulses" meta="Work that runs on a schedule and reports into the channel that needs it" data-id="element-869" data-mp="2g8hxr1y7t3ki" />
      <ul className="space-y-2.5" data-id="element-870" data-mp="2b8vd47g24l1o">
        {pulses.map(p => {
        const off = !!paused[p.name];
        const running = !off && 'running' in p ? (p as {
          running: string;
        }).running : null;
        return <li key={p.name} className={`flex flex-wrap items-center gap-3.5 px-4 py-3.5 ${ROW} ${running ? 'border-[#EA580C]/25 bg-[#FFF8F3]' : ''}`} data-id="element-871" data-mp="21ik96kwdibus">
              <span className="min-w-0 flex-1" data-id="element-872" data-mp="1sjoyavidwdl5">
                <span className={`block truncate text-[13.5px] font-semibold ${off ? 'text-[#8C7F73]' : 'text-[#2B211A]'}`} data-id="element-873" data-mp="1sl01ou7x3jbq">
                  {p.name}
                </span>
                {/* While a pulse is mid-run, the row reports the run instead of
                 the schedule — the schedule is not the useful fact right now. */}
                {running ? <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] font-medium text-[#C2410C]" data-id="element-874" data-mp="1dwgi1ga7xi2b">
                    <Loader2 size={11} className="shrink-0 animate-spin" data-id="element-875" data-mp="zxqogamly0ip" />
                    <span className="truncate" data-id="element-876" data-mp="13yk8gswxiv6b">{running}</span>
                  </span> : <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-877" data-mp="140mz9f5wosc3">
                    {p.cadence}
                    <span className="text-[#D8CFC2]" data-id="element-878" data-mp="1wmy9lhjbe7m3">·</span>
                    <BrandLogo slug={p.logo} className={`size-3.5 ${off ? 'opacity-40' : ''}`} data-id="element-879" data-mp="1wmg3lt6uof0z" />
                    <span className="truncate" data-id="element-880" data-mp="1ivktyeoj1twc">{p.to}</span>
                  </span>}
              </span>
              {/* Twelve ticks say "is this healthy" faster than a percentage. */}
              <span className="flex shrink-0 items-end gap-[3px] rounded-md bg-white px-1.5 py-1 ring-1 ring-[#F1ECE3]" data-id="element-881" data-mp="2alfuz8lqz80u">
                {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${off ? 'bg-[#E2DACE]' : h === 1 ? 'bg-emerald-500/80' : h === 0 ? 'bg-red-500/85' : 'bg-amber-500/85'}`} data-id="element-882" data-mp="3yqi187ezb3w" data-mpi={i} />)}
              </span>
              <span className="flex w-[92px] shrink-0 items-center justify-end gap-1.5 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-883" data-mp="dojt883n9o7m">
                {off ? <span className="text-neutral-400" data-id="element-884" data-mp="8pywiinlpmnx">Paused</span> : running ? <span className="font-semibold text-[#C2410C]" data-id="element-885" data-mp="3h4qphtjqcj6d">Running</span> : <>
                    <Clock size={11} className="text-neutral-300" data-id="element-886" data-mp="3c6fdpd9vjyli" />
                    {p.next}
                  </>}
              </span>
              <button type="button" onClick={() => setPaused(s => ({
            ...s,
            [p.name]: !off
          }))} aria-label={off ? `Resume ${p.name}` : `Pause ${p.name}`} className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-[#EFEAE1] bg-white text-[#6B5D52] transition-colors duration-150 ease-out hover:border-[#D8CFC2] hover:text-[#2B211A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/40" data-id="element-887" data-mp="3r4hmq2zodca5">
                {off ? <Play size={12} fill="currentColor" data-id="element-888" data-mp="2zveni180l8ji" /> : <Pause size={12} fill="currentColor" data-id="element-889" data-mp="2zwhcu4pov2xb" />}
              </button>
            </li>;
      })}
      </ul>
    </div>;
}

// One sparkle on every row said "AI feature" four times and nothing about the
// skills themselves. A glyph per subject makes the list scannable by what each
// method is for, and the ink tile matches the group tiles on the Groups screen
// so both screens read as the same application.
export function SkillsScreen() {
  // Named as the piece of knowledge itself ("Inbound lead qualification")
  // rather than as a sentence about it ("How we qualify an inbound lead") —
  // a library of nouns scans; a list of sentences has to be read.
  const skills = [{
    name: 'Inbound lead qualification',
    icon: Filter,
    used: 142,
    group: 'Marketing',
    by: 'Priya Nair',
    initials: 'PN',
    color: '#7C2D12',
    created: 'Jun 4'
  }, {
    name: 'Weekly reporting format',
    icon: BarChart3,
    used: 96,
    group: 'Leadership',
    by: 'Sarah Chen',
    initials: 'SC',
    color: '#1E3A5F',
    created: 'May 12'
  }, {
    name: 'P1 incident escalation',
    icon: Siren,
    used: 38,
    group: 'Customer Success',
    by: 'Tom Alvarez',
    initials: 'TA',
    color: '#3F3A2E',
    created: 'Apr 28'
  }, {
    name: 'Renewal email drafting',
    icon: Mail,
    used: 211,
    group: 'Sales',
    by: 'Marcus Lee',
    initials: 'ML',
    color: '#134E4A',
    created: 'Mar 19'
  }];
  return <div data-id="element-890" data-mp="1znrfpr4icbid">
      <ScreenHead title="Skills" meta="How your company does something, written once and reused everywhere" data-id="element-891" data-mp="1znhoy1bn20bm" />
      <ul className="space-y-2.5" data-id="element-892" data-mp="1up6fhrp99rtx">
        {skills.map(s => <li key={s.name} className={`flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-893" data-mp="1uoepqxicaobk">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-894" data-mp="oa5om0f37zu7">
              <s.icon size={15} strokeWidth={1.9} className="text-white" data-id="element-895" data-mp="3do12id62odzc" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-896" data-mp="38p7nggb50ex2">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-897" data-mp="38mbrgxiuo0b9">
                {s.name}
              </span>
              {/* Who wrote the method, and which group it belongs to. A skill
               is somebody's expertise made reusable, so the person is the
               credential — freshness matters far less than authorship here. */}
              <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-898" data-mp="3re4hcj2mh5er">
                <span className="flex size-[17px] shrink-0 items-center justify-center rounded-full text-[7px] font-bold text-white" style={{
              backgroundColor: s.color
            }} data-id="element-899" data-mp="a60tcuos62k5">
                  {s.initials}
                </span>
                <span className="truncate text-[#6B5D52]" data-id="element-900" data-mp="26z2nn6smpvwx">{s.by}</span>
                <span className="text-[#D8CFC2]" data-id="element-901" data-mp="2715koas78pb5">·</span>
                <span className="truncate" data-id="element-902" data-mp="mhth3n3st0f2">{s.group}</span>
                <span className="text-[#D8CFC2]" data-id="element-903" data-mp="rfv5mnjztrxx">·</span>
                <span className="shrink-0 whitespace-nowrap" data-id="element-904" data-mp="rfbrzj1dlppp">{s.created}</span>
              </span>
            </span>
            {/* Reuse count as a stat, not a badge: the number is the point, so
             it carries the weight and the unit sits under it in small caps. */}
            <span className="shrink-0 text-right" data-id="element-905" data-mp="3p7l92sw59uh">
              <span className="block text-[15px] font-semibold tabular-nums leading-none text-[#2B211A]" data-id="element-906" data-mp="3sn0q6qh8kwf">
                {s.used}
              </span>
              <span className="mt-1 block text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-907" data-mp="3pyse0xsc8jay">
                uses
              </span>
            </span>
          </li>)}
      </ul>
    </div>;
}